import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import styled from 'styled-components';

import { login } from 'services/api-service/center';
import { CookieService } from 'services/cookie-service';
import { ActionCtorUser } from 'store/modules/user';
import Footer from './Footer';

const AUTH_EXPIRED_CODE = 1026;

const Login = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [checked, setChecked] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const { register, handleSubmit, setFocus, setValue } = useForm();

  const onSubmit = (data) => {
    setErrorMessage('');
    fetchLogin(data);
  };

  const fetchLogin = async (data: any) => {
    try {
      const response: any = await login(data.id, data.password);
      if (response.result === 'success') {
        if (checked) {
          CookieService.getInstance().setId(data.id, undefined);
        } else {
          CookieService.getInstance().removeId();
        }
        const userDataWithId = {
          ...response,
          userId: data.id,
        };

        dispatch(ActionCtorUser.setUser(userDataWithId));
        sessionStorage.setItem('isAuthenticated', 'true');
        navigate('/center');
      } else {
        sessionStorage.setItem('isAuthenticated', 'false');
        if (response.error_code === AUTH_EXPIRED_CODE) {
          toast.success('비밀번호 변경 후 이용바랍니다.');
          navigate('/change', { state: { userId: data.id } });
        } else {
          CookieService.getInstance().removeId();
          setErrorMessage(response.error_msg);
        }
      }
    } catch (error) {
      CookieService.getInstance().removeId();
      sessionStorage.setItem('isAuthenticated', 'false');
      setErrorMessage('서버와의 통신 중 오류가 발생했습니다.');
      console.error('fetchLogin ', error);
    }
  };

  const onChange = (checked: boolean) => {
    setChecked(checked);
  };

  useEffect(() => {
    if (CookieService.getInstance().getId() === undefined || CookieService.getInstance().getId() === '') {
      setChecked(false);
    } else {
      setChecked(true);
      setValue('id', CookieService.getInstance().getId(), { shouldValidate: true });
    }
    setFocus('id');
  }, []);

  return (
    <LoginTemplateWrap>
      <CenterWrap>
        <Logo>
          <img src={process.env.PUBLIC_URL + '/images/login_logo.svg'} alt="logo" />
          <TitleText>로그인</TitleText>
        </Logo>

        <ErrorText>{errorMessage}</ErrorText>
        <form onSubmit={handleSubmit(onSubmit)}>
          <HorizontalWrap>
            <ColorWrap />
            <ImageWrap>
              <img src={process.env.PUBLIC_URL + '/images/icon_id.svg'} alt="id" />
            </ImageWrap>
            <InputBox placeholder="ID" {...register('id', { required: true, maxLength: 20 })} />
          </HorizontalWrap>
          <HorizontalWrap>
            <ColorWrap />
            <ImageWrap>
              <img src={process.env.PUBLIC_URL + '/images/icon_pw.svg'} alt="pw" />
            </ImageWrap>
            <InputBox placeholder="PW" type="password" {...register('password', { required: true, maxLength: 20 })} />
          </HorizontalWrap>
          <InputLabel>
            <input type="checkbox" checked={checked} onChange={({ target: { checked } }) => onChange(checked)} />
            <InputText>아이디 저장</InputText>
          </InputLabel>
          <SubmitButton type="submit" value={'로그인'} />
        </form>
      </CenterWrap>
      <Footer />
    </LoginTemplateWrap>
  );
};

const LoginTemplateWrap = styled.div`
  width: 100%;
  min-height: 100%;
  background: #002343;
`;
const CenterWrap = styled.div`
  position: absolute;
  width: 28.1875rem;
  height: 26.8125rem;
  background: #002343;
  top: 40%;
  left: 50%;
  transform: translate(-50%, -40%);
  justify-content: center;
  align-items: center;
`;
const Logo = styled.div`
  text-align: center;
  margin-bottom: 1.87rem;
`;
const TitleText = styled.h1`
  display: flex;
  width: 28.1875rem;
  height: 3rem;
  margin-top: 1rem;
  flex-direction: column;
  justify-content: center;
  color: #fff;
  text-align: center;
  font-family: Inter;
  font-size: 1.25rem;
  font-style: normal;
  font-weight: 700;
  line-height: normal;
`;
const ErrorText = styled.div`
  display: flex;
  width: 28.1875rem;
  height: 3rem;
  flex-direction: column;
  justify-content: center;
  color: #ffd900;
  text-align: center;
  font-family: Inter;
  font-size: 1rem;
  font-weight: 400;
  line-height: normal;
  visibility: ${(props) => (props.children ? 'visible' : 'hidden')};
`;
const HorizontalWrap = styled.div`
  display: flex;
  width: 28.1875rem;
  height: 3rem;
  justify-content: center;
  align-items: flex-start;
  margin-bottom: 20px;
  background: #f5f5f5;
`;
const ColorWrap = styled.div`
  width: 0.375rem;
  height: 3rem;
  background: #e63528;
`;
const ImageWrap = styled.div`
  width: 3rem;
  height: 3rem;
  display: flex;
  justify-content: center;
  align-items: center;
`;
const InputBox = styled.input`
  width: 100%;
  height: 3rem;
  margin-left: 0.81rem;
  padding: 0.8em 0.5em;
  border: none;
  color: #000;
  font-family: Inter;
  font-size: 0.875rem;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  background: #f5f5f5;
  &::placeholder {
    color: #a5a5a5;
  }
  &:focus {
    outline: none;
  }
  /* Chrome, Safari, Edge, Opera */
  ::-webkit-outer-spin-button,
  ::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
`;
const InputLabel = styled.label`
  display: flex;
  margin-top: 2rem;
`;
const InputText = styled.h1`
  color: #fff;
  font-family: Inter;
  font-size: 0.875rem;
  font-style: normal;
  font-weight: 500;
  line-height: normal;
`;
const SubmitButton = styled.input`
  position: absolute;
  bottom: 0;
  width: 28.1875rem;
  height: 3rem;
  border-radius: 1.47413rem;
  background: #e63528;
  border: 1px solid #e63528;
  cursor: pointer;
  color: #fff;
  text-align: center;
  font-family: Inter;
  font-size: 1rem;
  font-style: normal;
  font-weight: 700;
  line-height: normal;
`;

export default Login;
